import type { LegalConsentItem } from './types'

export type LegalConsentRecord = {
  identifier: string
  acceptedAt: string
}

/**
 * True when any required, pre-onboarding-context item has never been
 * accepted, or its latest acceptance predates the item's own
 * `lastModifiedAt`. Unlike a single shared `acceptedAt`, `Company.legalConsents`
 * is an append-only audit log that can hold multiple records per
 * `identifier` over time — this takes the latest one. Optional items and
 * onboarding-only items never factor in: an optional item was never
 * mandatory, and onboarding is a one-time flow, not a page that gets
 * "re-shown".
 */
export function needsReacceptance(
  records: LegalConsentRecord[],
  items: LegalConsentItem[]
): boolean {
  const latestAcceptedAtMsByIdentifier = new Map<string, number>()

  for (const record of records) {
    const ms = new Date(record.acceptedAt).getTime()
    if (Number.isNaN(ms)) continue

    const existing = latestAcceptedAtMsByIdentifier.get(record.identifier)
    if (existing === undefined || ms > existing) {
      latestAcceptedAtMsByIdentifier.set(record.identifier, ms)
    }
  }

  const requiredPreOnboardingItems = items.filter(
    (item) => item.required !== false && item.context.includes('pre-onboarding')
  )

  return requiredPreOnboardingItems.some((item) => {
    const lastModifiedMs = new Date(item.lastModifiedAt).getTime()
    if (Number.isNaN(lastModifiedMs)) return false

    const acceptedMs = latestAcceptedAtMsByIdentifier.get(item.identifier)
    return acceptedMs === undefined || acceptedMs < lastModifiedMs
  })
}
